import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import Alert from './Alert';
import Button from './Button';
import CustomerSelect from './CustomerSelect';
import './ROICalculator.css';

interface Customer {
  customer_tag: string;
  description: string;
  stripe: {
    name: string;
  };
}

interface UsageData {
  escalations?: number;
  escalation_categories?: Record<string, number>;
  reports?: number;
  videos_total?: number;
  video_duration?: { days: number; hours: number; minutes: number };
  activation_rate?: number;
  actions?: Record<string, number>;
}

interface DeviceUsage {
  dau?: number;
  wau?: number;
  mau?: number;
  total_devices?: number;
  utilization?: number;
}

interface ROIData {
  customer_tag: string;
  org_name: string;
  subscription: { contract_units?: number; monthly_rate?: number };
  usage: UsageData;
  devices?: DeviceUsage;
}

interface TimeDriver {
  id: string;
  label: string;
  beforeMin: number;
  withPlixMin: number;
  withPlixLabel: string;
  enabled: boolean;
}

interface CostDriver {
  id: string;
  label: string;
  beforeValue: number;
  unit: string;
  enabled: boolean;
}

interface RiskDriver {
  id: string;
  label: string;
  beforeValue: number;
  pctValue: number;
  unit: string;
  pctUnit: string;
  enabled: boolean;
}

const TIME_PRESETS: Record<string, number[]> = {
  reports: [10, 15, 20, 30, 45],
  checkout: [5, 10, 15, 20],
  video_search: [10, 15, 20, 30],
  shift_review: [10, 15, 20, 30],
  compliance: [10, 15, 20, 30],
};

const COST_PRESETS: Record<string, number[]> = {
  equipment: [200, 500, 1000, 2000],
};

const RISK_VALUE_PRESETS: Record<string, number[]> = {
  insurance: [1000, 2000, 5000, 10000],
  incident: [5000, 10000, 15000, 25000],
};

const DEFAULT_TIME_DRIVERS: TimeDriver[] = [
  { id: 'reports', label: 'Daily activity reports', beforeMin: 20, withPlixMin: 0.75, withPlixLabel: 'Voice-to-report AI \u2014 45 sec', enabled: false },
  { id: 'checkout', label: 'Equipment checkout', beforeMin: 10, withPlixMin: 0.5, withPlixLabel: 'QR scan \u2014 30 sec', enabled: false },
  { id: 'video_search', label: 'Incident video search', beforeMin: 15, withPlixMin: 0.5, withPlixLabel: 'AI search + transcript \u2014 30 sec', enabled: false },
  { id: 'shift_review', label: 'Supervisor shift review', beforeMin: 20, withPlixMin: 2, withPlixLabel: 'AI shift summary \u2014 2 min', enabled: false },
  { id: 'compliance', label: 'Compliance documentation', beforeMin: 15, withPlixMin: 1, withPlixLabel: 'Auto-generated \u2014 1 min', enabled: false },
];

const DEFAULT_COST_DRIVERS: CostDriver[] = [
  { id: 'equipment', label: 'Equipment replacement', beforeValue: 500, unit: '$/mo', enabled: false },
  { id: 'overtime', label: 'Overtime for reporting', beforeValue: 0, unit: 'calculated', enabled: false },
];

const DEFAULT_RISK_DRIVERS: RiskDriver[] = [
  { id: 'insurance', label: 'Insurance premium', beforeValue: 2000, pctValue: 15, unit: '$/mo', pctUnit: '% reduction', enabled: false },
  { id: 'incident', label: 'Incident cost avoidance', beforeValue: 15000, pctValue: 5, unit: '$/incident', pctUnit: '% of escalations', enabled: false },
];

const ROICalculator: React.FC = () => {
  const { username } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roiData, setRoiData] = useState<ROIData | null>(null);

  // Champion deck generation
  const [generatingDeck, setGeneratingDeck] = useState(false);
  const [deckUrl, setDeckUrl] = useState<string | null>(null);
  const [deckError, setDeckError] = useState<string | null>(null);

  // Quick inputs
  const [workers, setWorkers] = useState(10);
  const [hourlyWage, setHourlyWage] = useState(18);
  const [shiftLength, setShiftLength] = useState(8);
  const [monthlyRate, setMonthlyRate] = useState(125);

  // Value drivers
  const [timeDrivers, setTimeDrivers] = useState<TimeDriver[]>(DEFAULT_TIME_DRIVERS);
  const [costDrivers, setCostDrivers] = useState<CostDriver[]>(DEFAULT_COST_DRIVERS);
  const [riskDrivers, setRiskDrivers] = useState<RiskDriver[]>(DEFAULT_RISK_DRIVERS);

  // Collapsed sections (all collapsed by default)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    time: false,
    cost: false,
    risk: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Presentation mode
  const [presentationMode, setPresentationMode] = useState(false);

  const togglePresentationMode = useCallback(() => {
    const next = !presentationMode;
    setPresentationMode(next);
    document.documentElement.classList.toggle('presentation-mode', next);
  }, [presentationMode]);

  useEffect(() => {
    return () => document.documentElement.classList.remove('presentation-mode');
  }, []);

  // Fetch customers for datalist
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/customers`);
        if (!response.ok) throw new Error('Failed to fetch customers');
        const data = await response.json();
        setCustomers(data.customers || []);
      } catch {
        // Silently fail — datalist is optional
      }
    };
    fetchCustomers();
  }, []);

  const loadData = useCallback(async (customerTag: string) => {
    if (!customerTag) return;

    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/customer/${encodeURIComponent(customerTag)}/roi-data`);
      if (!resp.ok) {
        const errData = await resp.json();
        throw new Error(errData.error || 'Failed to load data');
      }
      const data: ROIData = await resp.json();
      setRoiData(data);

      // Default Personnel to actual deployed devices when available (closer to
      // real headcount than Stripe contract quantity), and fall back to the
      // contract quantity otherwise.
      const personnel = data.devices?.total_devices || data.subscription?.contract_units || 0;
      if (personnel > 0) {
        setWorkers(personnel);
      }
      if (data.subscription?.monthly_rate) {
        setMonthlyRate(data.subscription.monthly_rate);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load data when customer changes
  const handleCustomerChange = useCallback((value: string) => {
    setSelectedCustomer(value);
    if (value) {
      loadData(value);
    } else {
      setRoiData(null);
    }
  }, [loadData]);

  const [customIdCounter, setCustomIdCounter] = useState(0);

  const removeTimeDriver = (id: string) => setTimeDrivers(prev => prev.filter(d => d.id !== id));
  const removeCostDriver = (id: string) => setCostDrivers(prev => prev.filter(d => d.id !== id));
  const removeRiskDriver = (id: string) => setRiskDrivers(prev => prev.filter(d => d.id !== id));

  const addTimeDriver = () => {
    const id = `custom_time_${customIdCounter}`;
    setCustomIdCounter(c => c + 1);
    setTimeDrivers(prev => [...prev, { id, label: 'Custom task', beforeMin: 15, withPlixMin: 1, withPlixLabel: 'With Plix', enabled: true }]);
    if (!expandedSections.time) setExpandedSections(prev => ({ ...prev, time: true }));
  };

  const addCostDriver = () => {
    const id = `custom_cost_${customIdCounter}`;
    setCustomIdCounter(c => c + 1);
    setCostDrivers(prev => [...prev, { id, label: 'Custom cost', beforeValue: 200, unit: '$/mo', enabled: true }]);
    if (!expandedSections.cost) setExpandedSections(prev => ({ ...prev, cost: true }));
  };

  const addRiskDriver = () => {
    const id = `custom_risk_${customIdCounter}`;
    setCustomIdCounter(c => c + 1);
    setRiskDrivers(prev => [...prev, { id, label: 'Custom risk', beforeValue: 5000, pctValue: 10, unit: '$/incident', pctUnit: '% reduction', enabled: true }]);
    if (!expandedSections.risk) setExpandedSections(prev => ({ ...prev, risk: true }));
  };

  // Update time driver label
  const updateTimeDriverLabel = (id: string, label: string) => {
    setTimeDrivers(prev => prev.map(d => d.id === id ? { ...d, label } : d));
  };

  const updateCostDriverLabel = (id: string, label: string) => {
    setCostDrivers(prev => prev.map(d => d.id === id ? { ...d, label } : d));
  };

  const updateRiskDriverLabel = (id: string, label: string) => {
    setRiskDrivers(prev => prev.map(d => d.id === id ? { ...d, label } : d));
  };

  // Update time driver
  const updateTimeDriver = (id: string, field: 'enabled' | 'beforeMin' | 'withPlixMin', value: boolean | number) => {
    setTimeDrivers(prev => prev.map(d => d.id === id ? { ...d, [field]: value } : d));
  };

  // Update cost driver
  const updateCostDriver = (id: string, field: 'enabled' | 'beforeValue', value: boolean | number) => {
    setCostDrivers(prev => prev.map(d => d.id === id ? { ...d, [field]: value } : d));
  };

  // Update risk driver
  const updateRiskDriver = (id: string, field: 'enabled' | 'beforeValue' | 'pctValue', value: boolean | number) => {
    setRiskDrivers(prev => prev.map(d => d.id === id ? { ...d, [field]: value } : d));
  };

  // Calculations
  const results = useMemo(() => {
    const shiftsPerMonth = 30 / shiftLength * 8; // normalize to 8-hour equivalent shifts per month

    // Time savings
    const timeSavedMinutes = timeDrivers
      .filter(d => d.enabled)
      .reduce((sum, d) => sum + (d.beforeMin - d.withPlixMin), 0);
    const timeSavedHoursPerMonth = (timeSavedMinutes / 60) * shiftsPerMonth;
    const timeSavingsDollars = timeSavedHoursPerMonth * hourlyWage * workers;

    // Operations cost savings
    const equipmentDriver = costDrivers.find(d => d.id === 'equipment');
    const equipmentSavings = (equipmentDriver?.enabled ? equipmentDriver.beforeValue : 0);
    const overtimeDriver = costDrivers.find(d => d.id === 'overtime');
    const overtimeSavings = overtimeDriver?.enabled ? (timeSavedHoursPerMonth * hourlyWage * 1.5 * 0.3) : 0;

    // Risk reduction
    const insuranceDriver = riskDrivers.find(d => d.id === 'insurance');
    const insuranceSavings = insuranceDriver?.enabled
      ? (insuranceDriver.beforeValue * insuranceDriver.pctValue / 100)
      : 0;

    const incidentDriver = riskDrivers.find(d => d.id === 'incident');
    const escalations = roiData?.usage?.escalations || 10;
    const incidentAvoidance = incidentDriver?.enabled
      ? (escalations * (incidentDriver.pctValue / 100) * incidentDriver.beforeValue / 12)
      : 0;

    const totalMonthlySavings = timeSavingsDollars + equipmentSavings + overtimeSavings + insuranceSavings + incidentAvoidance;
    const monthlyPlixCost = workers * monthlyRate;
    const netMonthlyBenefit = totalMonthlySavings - monthlyPlixCost;
    const roiPct = monthlyPlixCost > 0 ? (netMonthlyBenefit / monthlyPlixCost) * 100 : 0;
    const savingsPerWorker = workers > 0 ? totalMonthlySavings / workers : 0;
    const paybackMonths = totalMonthlySavings > 0 ? Math.min(monthlyPlixCost / totalMonthlySavings * 12, 12) : 12;

    return {
      timeSavedMinutes,
      timeSavedHoursPerMonth,
      timeSavingsDollars,
      equipmentSavings,
      overtimeSavings,
      insuranceSavings,
      incidentAvoidance,
      totalMonthlySavings,
      monthlyPlixCost,
      netMonthlyBenefit,
      roiPct,
      savingsPerWorker,
      paybackMonths,
    };
  }, [workers, hourlyWage, shiftLength, monthlyRate, timeDrivers, costDrivers, riskDrivers, roiData]);

  const fmt = (n: number) => Math.round(n).toLocaleString();
  const fmtDec = (n: number) => n.toFixed(1);

  const hasData = workers > 0;

  const enabledTimeCount = timeDrivers.filter(d => d.enabled).length;
  const enabledCostCount = costDrivers.filter(d => d.enabled).length;
  const enabledRiskCount = riskDrivers.filter(d => d.enabled).length;

  const formatMinutes = (min: number) => {
    if (min >= 1) return `${min} min`;
    const sec = Math.round(min * 60);
    return `${sec} sec`;
  };

  const handleGenerateDeck = useCallback(async () => {
    setGeneratingDeck(true);
    setDeckError(null);
    setDeckUrl(null);
    try {
      const customerName =
        customers.find(c => c.customer_tag === selectedCustomer)?.description
        || roiData?.org_name
        || selectedCustomer
        || 'Prospect';

      const enabledTime = timeDrivers.filter(d => d.enabled);
      const timeDriversPayload = enabledTime.map(driver => {
        const saved = driver.beforeMin - driver.withPlixMin;
        const dollars = (saved / 60) * 30 * hourlyWage * workers;
        return {
          label: driver.label,
          before: `${driver.beforeMin} min`,
          after: formatMinutes(driver.withPlixMin),
          value: `$${fmt(dollars)}`,
        };
      });

      // Cost drivers: only the two with computed savings ('equipment', 'overtime') for v1.
      const costDriversPayload = costDrivers
        .filter(d => d.enabled && (d.id === 'equipment' || d.id === 'overtime'))
        .map(d => ({
          label: d.label,
          value: `$${fmt(d.id === 'equipment' ? results.equipmentSavings : results.overtimeSavings)}/mo`,
        }));

      // Risk drivers: only the two with computed savings ('insurance', 'incident') for v1.
      const riskDriversPayload = riskDrivers
        .filter(d => d.enabled && (d.id === 'insurance' || d.id === 'incident'))
        .map(d => ({
          label: d.label,
          value: `$${fmt(d.id === 'insurance' ? results.insuranceSavings : results.incidentAvoidance)}/mo`,
        }));

      const payload = {
        customer_tag: selectedCustomer || null,
        customer_name: customerName,
        ae_name: username || '',
        assumptions: { personnel: workers, hourly_wage: hourlyWage, monthly_rate: monthlyRate },
        totals: {
          savings_per_person: results.savingsPerWorker,
          roi_pct: results.roiPct,
          annual_value: results.totalMonthlySavings * 12,
          payback_months: results.paybackMonths,
          total_monthly_savings: results.totalMonthlySavings,
          monthly_plix_cost: results.monthlyPlixCost,
        },
        time: { savings_dollars: results.timeSavingsDollars, drivers: timeDriversPayload },
        cost: { total: results.equipmentSavings + results.overtimeSavings, drivers: costDriversPayload },
        risk: { total: results.insuranceSavings + results.incidentAvoidance, drivers: riskDriversPayload },
        usage: {
          devices_deployed: roiData?.devices?.total_devices ?? null,
          mau: roiData?.devices?.mau ?? null,
          utilization_pct: roiData?.devices?.utilization ?? null,
          escalations: roiData?.usage?.escalations ?? null,
          reports_generated: roiData?.usage?.reports ?? null,
          activation_rate_pct:
            roiData?.usage?.activation_rate != null
              ? Math.round(roiData.usage.activation_rate * 100)
              : null,
        },
      };

      const resp = await fetch(`${API_BASE_URL}/api/champion-deck/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.error || `Generation failed (${resp.status})`);
      }
      const data = await resp.json();
      setDeckUrl(data.doc_url);
    } catch (e) {
      setDeckError(e instanceof Error ? e.message : 'Generation failed');
    } finally {
      setGeneratingDeck(false);
    }
  }, [customers, selectedCustomer, roiData, username, workers, hourlyWage, monthlyRate, results, timeDrivers, costDrivers, riskDrivers]);

  const canGenerateDeck = hasData && (
    timeDrivers.some(d => d.enabled) ||
    costDrivers.some(d => d.enabled) ||
    riskDrivers.some(d => d.enabled)
  );

  return (
    <div className="roi-calculator">
      <div className="roi-layout">
        {/* LEFT: Inputs */}
        <div className="roi-input-panel">
          <Alert type="error">{error}</Alert>

          <div className="roi-section">
            <h3>Customer</h3>
            <CustomerSelect
              value={selectedCustomer}
              onChange={handleCustomerChange}
              options={customers.map(c => ({
                value: c.customer_tag,
                label: c.description || c.stripe?.name || c.customer_tag,
              }))}
              placeholder="Search customers..."
            />
            {loading && <div className="roi-loading-hint">Loading usage data...</div>}
          </div>

          <div className="roi-quick-inputs">
            <div className="form-group">
              <label>Personnel</label>
              <input type="number" value={workers} onChange={e => setWorkers(parseInt(e.target.value) || 0)} min="1" />
            </div>
            <div className="form-group">
              <label>Hourly Wage ($)</label>
              <input type="number" value={hourlyWage} onChange={e => setHourlyWage(parseFloat(e.target.value) || 0)} min="1" />
            </div>
            <div className="form-group">
              <label>Shift (hrs)</label>
              <input type="number" value={shiftLength} onChange={e => setShiftLength(parseFloat(e.target.value) || 8)} min="1" max="24" />
            </div>
            <div className="form-group">
              <label>Plix $/mo</label>
              <input type="number" value={monthlyRate} onChange={e => setMonthlyRate(parseFloat(e.target.value) || 0)} min="1" />
            </div>
          </div>

          {/* Personnel Productivity */}
          <div className="roi-section">
            <div className="roi-section-header" onClick={() => toggleSection('time')}>
              <span className={`roi-section-chevron ${expandedSections.time ? 'open' : ''}`}>{expandedSections.time ? '\u25BC' : '\u25B6'}</span>
              <span className="roi-section-title">Personnel Productivity</span>
              <span className="roi-section-subtitle">time savings per shift</span>
              {enabledTimeCount > 0 && <span className="roi-section-count">{enabledTimeCount}/{timeDrivers.length}</span>}
            </div>
            {expandedSections.time && <><div className="roi-driver-cards">
              {timeDrivers.map(driver => (
                <div
                  key={driver.id}
                  className={`roi-driver-card ${driver.enabled ? 'enabled' : ''}`}
                  onClick={(e) => { if ((e.target as HTMLElement).tagName !== 'INPUT') updateTimeDriver(driver.id, 'enabled', !driver.enabled); }}
                >
                  <div className="roi-driver-card-top">
                    {driver.id.startsWith('custom_') ? (<>
                      <input className="roi-editable-label" type="text" value={driver.label}
                        onChange={e => updateTimeDriverLabel(driver.id, e.target.value)}
                        onClick={e => e.stopPropagation()} placeholder="Task name" />
                      <button className="roi-remove-btn" type="button"
                        onClick={e => { e.stopPropagation(); removeTimeDriver(driver.id); }}>&times;</button>
                    </>) : (
                      <span className="roi-driver-card-label">{driver.label}</span>
                    )}
                  </div>
                  <div className="roi-driver-card-detail">
                    <div className="roi-driver-before-block">
                      <span className="roi-driver-block-label">Today (min/shift)</span>
                      <div className="roi-presets">
                        {(TIME_PRESETS[driver.id] || [10, 15, 20, 30]).map(p => (
                          <button key={p} type="button"
                            className={`roi-preset-btn ${driver.beforeMin === p ? 'active' : ''}`}
                            onClick={e => { e.stopPropagation(); updateTimeDriver(driver.id, 'beforeMin', p); if (!driver.enabled) updateTimeDriver(driver.id, 'enabled', true); }}
                          >{p}</button>
                        ))}
                        <input
                          className="roi-preset-custom"
                          type="text"
                          inputMode="numeric"
                          value={(TIME_PRESETS[driver.id] || []).includes(driver.beforeMin) ? '' : driver.beforeMin || ''}
                          placeholder="Other"
                          onChange={e => {
                            const val = parseFloat(e.target.value) || 0;
                            updateTimeDriver(driver.id, 'beforeMin', val);
                            if (!driver.enabled) updateTimeDriver(driver.id, 'enabled', true);
                          }}
                          onClick={e => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <span className="roi-arrow">&rarr;</span>
                    <div className="roi-driver-after-block">
                      <span className="roi-driver-block-label">With Plix</span>
                      {driver.id.startsWith('custom_') ? (
                        <>
                          <input
                            className="roi-editable-value"
                            type="text"
                            inputMode="numeric"
                            value={driver.withPlixMin}
                            onChange={e => {
                              updateTimeDriver(driver.id, 'withPlixMin', parseFloat(e.target.value) || 0);
                            }}
                            onClick={e => e.stopPropagation()}
                          />
                          <span className="roi-driver-block-unit">min/shift</span>
                        </>
                      ) : (
                        <>
                          <span className="roi-fixed-value">{formatMinutes(driver.withPlixMin)}</span>
                          <span className="roi-driver-block-unit">{driver.withPlixLabel.split('\u2014')[0].trim()}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button className="roi-add-driver" onClick={addTimeDriver} type="button">+ Add item</button>
            </>}
          </div>

          {/* Operations Cost */}
          <div className="roi-section">
            <div className="roi-section-header" onClick={() => toggleSection('cost')}>
              <span className={`roi-section-chevron ${expandedSections.cost ? 'open' : ''}`}>{expandedSections.cost ? '\u25BC' : '\u25B6'}</span>
              <span className="roi-section-title">Operations Cost</span>
              <span className="roi-section-subtitle">monthly</span>
              {enabledCostCount > 0 && <span className="roi-section-count">{enabledCostCount}/{costDrivers.length}</span>}
            </div>
            {expandedSections.cost && <><div className="roi-driver-cards">
              {costDrivers.map(driver => (
                <div
                  key={driver.id}
                  className={`roi-driver-card ${driver.enabled ? 'enabled' : ''}`}
                  onClick={(e) => { if ((e.target as HTMLElement).tagName !== 'INPUT') updateCostDriver(driver.id, 'enabled', !driver.enabled); }}
                >
                  <div className="roi-driver-card-top">
                    {driver.id.startsWith('custom_') ? (<>
                      <input className="roi-editable-label" type="text" value={driver.label}
                        onChange={e => updateCostDriverLabel(driver.id, e.target.value)}
                        onClick={e => e.stopPropagation()} placeholder="Cost name" />
                      <button className="roi-remove-btn" type="button"
                        onClick={e => { e.stopPropagation(); removeCostDriver(driver.id); }}>&times;</button>
                    </>) : (
                      <span className="roi-driver-card-label">{driver.label}</span>
                    )}
                  </div>
                  <div className="roi-driver-card-detail">
                    <div className="roi-driver-before-block">
                      <span className="roi-driver-block-label">Today ($/mo)</span>
                      {driver.id === 'overtime' ? (
                        <span className="roi-fixed-value muted">Auto</span>
                      ) : (
                        <div className="roi-presets">
                          {(COST_PRESETS[driver.id] || [200, 500, 1000, 2000]).map(p => (
                            <button key={p} type="button"
                              className={`roi-preset-btn ${driver.beforeValue === p ? 'active' : ''}`}
                              onClick={e => { e.stopPropagation(); updateCostDriver(driver.id, 'beforeValue', p); if (!driver.enabled) updateCostDriver(driver.id, 'enabled', true); }}
                            >${p >= 1000 ? `${p/1000}k` : p}</button>
                          ))}
                          <input
                            className="roi-preset-custom"
                            type="text"
                            inputMode="numeric"
                            value={(COST_PRESETS[driver.id] || []).includes(driver.beforeValue) ? '' : driver.beforeValue || ''}
                            placeholder="Other"
                            onChange={e => {
                              const val = parseFloat(e.target.value) || 0;
                              updateCostDriver(driver.id, 'beforeValue', val);
                              if (!driver.enabled) updateCostDriver(driver.id, 'enabled', true);
                            }}
                            onClick={e => e.stopPropagation()}
                          />
                        </div>
                      )}
                    </div>
                    <span className="roi-arrow">&rarr;</span>
                    <div className="roi-driver-after-block">
                      <span className="roi-driver-block-label">With Plix</span>
                      <span className="roi-fixed-value">{driver.id === 'equipment' ? '$0' : 'Eliminated'}</span>
                      <span className="roi-driver-block-unit">{driver.id === 'equipment' ? 'Warranty covered' : ''}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button className="roi-add-driver" onClick={addCostDriver} type="button">+ Add item</button>
            </>}
          </div>

          {/* Risk Reduction */}
          <div className="roi-section">
            <div className="roi-section-header" onClick={() => toggleSection('risk')}>
              <span className={`roi-section-chevron ${expandedSections.risk ? 'open' : ''}`}>{expandedSections.risk ? '\u25BC' : '\u25B6'}</span>
              <span className="roi-section-title">Risk Reduction</span>
              {enabledRiskCount > 0 && <span className="roi-section-count">{enabledRiskCount}/{riskDrivers.length}</span>}
            </div>
            {expandedSections.risk && <><div className="roi-driver-cards">
              {riskDrivers.map(driver => (
                <div
                  key={driver.id}
                  className={`roi-driver-card ${driver.enabled ? 'enabled' : ''}`}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).tagName === 'INPUT') return;
                    updateRiskDriver(driver.id, 'enabled', !driver.enabled);
                  }}
                >
                  <div className="roi-driver-card-top">
                    {driver.id.startsWith('custom_') ? (<>
                      <input className="roi-editable-label" type="text" value={driver.label}
                        onChange={e => updateRiskDriverLabel(driver.id, e.target.value)}
                        onClick={e => e.stopPropagation()} placeholder="Risk factor" />
                      <button className="roi-remove-btn" type="button"
                        onClick={e => { e.stopPropagation(); removeRiskDriver(driver.id); }}>&times;</button>
                    </>) : (
                      <span className="roi-driver-card-label">{driver.label}</span>
                    )}
                  </div>
                  <div className="roi-driver-card-detail">
                    <div className="roi-driver-before-block">
                      <span className="roi-driver-block-label">Current ({driver.unit})</span>
                      <div className="roi-presets">
                        {(RISK_VALUE_PRESETS[driver.id] || [5000, 10000, 15000, 25000]).map(p => (
                          <button key={p} type="button"
                            className={`roi-preset-btn ${driver.beforeValue === p ? 'active' : ''}`}
                            onClick={e => { e.stopPropagation(); updateRiskDriver(driver.id, 'beforeValue', p); if (!driver.enabled) updateRiskDriver(driver.id, 'enabled', true); }}
                          >${p >= 1000 ? `${p/1000}k` : p}</button>
                        ))}
                        <input
                          className="roi-preset-custom"
                          type="text"
                          inputMode="numeric"
                          value={(RISK_VALUE_PRESETS[driver.id] || []).includes(driver.beforeValue) ? '' : driver.beforeValue || ''}
                          placeholder="Other"
                          onChange={e => {
                            const val = parseFloat(e.target.value) || 0;
                            updateRiskDriver(driver.id, 'beforeValue', val);
                            if (!driver.enabled) updateRiskDriver(driver.id, 'enabled', true);
                          }}
                          onClick={e => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <span className="roi-arrow">&rarr;</span>
                    <div className="roi-driver-after-block">
                      <span className="roi-driver-block-label">Reduction</span>
                      <input
                        className="roi-editable-value"
                        type="text"
                        inputMode="numeric"
                        value={driver.pctValue}
                        onChange={e => {
                          const val = parseFloat(e.target.value) || 0;
                          updateRiskDriver(driver.id, 'pctValue', val);
                          if (!driver.enabled) updateRiskDriver(driver.id, 'enabled', true);
                        }}
                        onClick={e => e.stopPropagation()}
                      />
                      <span className="roi-driver-block-unit">{driver.pctUnit}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button className="roi-add-driver" onClick={addRiskDriver} type="button">+ Add item</button>
            </>}
          </div>
        </div>

        {/* RIGHT: Results */}
        <div className="roi-results-panel">
          {hasData ? (
            <>
              <div className="roi-header-row">
                <h2>ROI for {customers.find(c => c.customer_tag === selectedCustomer)?.description || selectedCustomer || 'Prospect'}</h2>
                <div className="roi-header-actions">
                  <button
                    type="button"
                    className="roi-deck-btn"
                    onClick={handleGenerateDeck}
                    disabled={!canGenerateDeck || generatingDeck}
                    title={canGenerateDeck ? 'Generate a champion deck in Google Slides' : 'Enable at least one value driver first'}
                  >
                    {generatingDeck ? 'Generating…' : 'Generate Deck'}
                  </button>
                  <button
                    type="button"
                    className={`roi-share-live-btn ${presentationMode ? 'active' : ''}`}
                    onClick={togglePresentationMode}
                  >
                    {presentationMode ? 'Exit' : 'Share Live'}
                  </button>
                </div>
              </div>

              {(deckUrl || deckError) && (
                <div className={`roi-deck-result ${deckError ? 'error' : 'success'}`}>
                  {deckError
                    ? <span>Couldn’t generate deck: {deckError}</span>
                    : (
                      <>
                        <span>Champion deck ready.</span>
                        <a href={deckUrl!} target="_blank" rel="noopener noreferrer">Open in Google Slides ↗</a>
                      </>
                    )
                  }
                </div>
              )}

              {presentationMode && (
                <div className="roi-branding">Plix AI — Body Camera Intelligence Platform</div>
              )}

              {/* Hero metric */}
              <div className="roi-hero">
                <div className="roi-hero-label">Plix saves</div>
                <div className="roi-hero-amount">${fmt(results.savingsPerWorker)}/mo</div>
                <div className="roi-hero-sublabel">per person per month</div>
              </div>

              {/* Savings Breakdown — show the math */}
              <div className="roi-breakdown">
                {/* Time Savings */}
                {timeDrivers.some(d => d.enabled) && (
                  <div className="roi-breakdown-section">
                    <div className="roi-breakdown-header">
                      <span>Personnel Productivity</span>
                      <span className="roi-breakdown-total">${fmt(results.timeSavingsDollars)}/mo</span>
                    </div>
                    {timeDrivers.filter(d => d.enabled).map(driver => {
                      const saved = driver.beforeMin - driver.withPlixMin;
                      const shiftsPerMonth = 30;
                      const driverSavings = (saved / 60) * shiftsPerMonth * hourlyWage * workers;
                      return (
                        <div key={driver.id} className="roi-breakdown-line">
                          <div className="roi-breakdown-label">{driver.label}</div>
                          <div className="roi-breakdown-math">
                            {driver.beforeMin} min → {formatMinutes(driver.withPlixMin)} = <strong>{Math.round(saved)} min saved</strong>/shift
                          </div>
                          <div className="roi-breakdown-value">${fmt(driverSavings)}/mo</div>
                        </div>
                      );
                    })}
                    <div className="roi-breakdown-formula">
                      {fmtDec(results.timeSavedMinutes)} min/shift &times; {workers} people &times; 30 shifts &times; ${hourlyWage}/hr
                    </div>
                  </div>
                )}

                {/* Cost Savings */}
                {costDrivers.some(d => d.enabled) && (
                  <div className="roi-breakdown-section">
                    <div className="roi-breakdown-header">
                      <span>Operations Cost</span>
                      <span className="roi-breakdown-total">${fmt(results.equipmentSavings + results.overtimeSavings)}/mo</span>
                    </div>
                    {costDrivers.filter(d => d.enabled).map(driver => {
                      const val = driver.id === 'equipment' ? results.equipmentSavings : results.overtimeSavings;
                      return (
                        <div key={driver.id} className="roi-breakdown-line">
                          <div className="roi-breakdown-label">{driver.label}</div>
                          <div className="roi-breakdown-math">
                            {driver.id === 'equipment'
                              ? `$${driver.beforeValue}/mo eliminated — warranty covered`
                              : `${fmtDec(results.timeSavedHoursPerMonth)} hrs × $${hourlyWage} × 1.5× × 30%`}
                          </div>
                          <div className="roi-breakdown-value">${fmt(val)}/mo</div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Risk Reduction */}
                {riskDrivers.some(d => d.enabled) && (
                  <div className="roi-breakdown-section">
                    <div className="roi-breakdown-header">
                      <span>Risk Reduction</span>
                      <span className="roi-breakdown-total">${fmt(results.insuranceSavings + results.incidentAvoidance)}/mo</span>
                    </div>
                    {riskDrivers.filter(d => d.enabled).map(driver => {
                      const val = driver.id === 'insurance' ? results.insuranceSavings : results.incidentAvoidance;
                      return (
                        <div key={driver.id} className="roi-breakdown-line">
                          <div className="roi-breakdown-label">{driver.label}</div>
                          <div className="roi-breakdown-math">
                            {driver.id === 'insurance'
                              ? `$${fmt(driver.beforeValue)}/mo × ${driver.pctValue}% reduction`
                              : `${roiData?.usage?.escalations || 10} escalations × ${driver.pctValue}% × $${fmt(driver.beforeValue)} ÷ 12`}
                          </div>
                          <div className="roi-breakdown-value">${fmt(val)}/mo</div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* No drivers selected */}
                {!timeDrivers.some(d => d.enabled) && !costDrivers.some(d => d.enabled) && !riskDrivers.some(d => d.enabled) && (
                  <div className="roi-breakdown-empty">
                    Select value drivers on the left to build the ROI story
                  </div>
                )}
              </div>

              {/* Bottom line */}
              {(results.totalMonthlySavings > 0) && (
                <div className="roi-bottom-line">
                  <div className="roi-bottom-row">
                    <span>Total Monthly Savings</span>
                    <strong className="positive">${fmt(results.totalMonthlySavings)}</strong>
                  </div>
                  <div className="roi-bottom-row">
                    <span>Monthly Plix Investment ({workers} × ${monthlyRate})</span>
                    <strong>${fmt(results.monthlyPlixCost)}</strong>
                  </div>
                  <div className="roi-bottom-row net">
                    <span>Net Monthly Benefit</span>
                    <strong className="positive">${fmt(results.netMonthlyBenefit)}</strong>
                  </div>
                  <div className="roi-bottom-metrics">
                    <div><span className="roi-metric-big positive">{fmt(results.roiPct)}%</span><span className="roi-metric-label">ROI</span></div>
                    <div><span className="roi-metric-big">${fmt(results.totalMonthlySavings * 12)}</span><span className="roi-metric-label">Annual Value</span></div>
                    <div><span className="roi-metric-big">{fmtDec(results.paybackMonths)} mo</span><span className="roi-metric-label">Payback</span></div>
                  </div>
                </div>
              )}

              {/* Platform Usage (only when data loaded) */}
              {roiData && (
                roiData.devices?.total_devices ||
                roiData.usage?.escalations ||
                roiData.usage?.reports
              ) ? (
                <div className="roi-usage-section">
                  <h3>Platform Usage (Live Data){roiData.org_name ? <span className="roi-usage-org"> &middot; {roiData.org_name}</span> : null}</h3>
                  {roiData.devices && (roiData.devices.total_devices || roiData.devices.mau) ? (
                    <div className="roi-usage-stats">
                      {roiData.devices.total_devices != null && (
                        <div className="roi-usage-stat">
                          <strong>{roiData.devices.total_devices.toLocaleString()}</strong> devices deployed
                        </div>
                      )}
                      {roiData.devices.dau != null && (
                        <div className="roi-usage-stat">
                          <strong>{roiData.devices.dau}</strong> DAU
                        </div>
                      )}
                      {roiData.devices.wau != null && (
                        <div className="roi-usage-stat">
                          <strong>{roiData.devices.wau.toLocaleString()}</strong> WAU
                        </div>
                      )}
                      {roiData.devices.mau != null && (
                        <div className="roi-usage-stat">
                          <strong>{roiData.devices.mau.toLocaleString()}</strong> MAU
                        </div>
                      )}
                      {roiData.devices.utilization != null && (
                        <div className="roi-usage-stat">
                          <strong>{roiData.devices.utilization}%</strong> utilization
                        </div>
                      )}
                    </div>
                  ) : null}
                  <div className="roi-usage-stats">
                    {roiData.usage?.escalations != null && (
                      <div className="roi-usage-stat">
                        <strong>{roiData.usage.escalations.toLocaleString()}</strong> escalations auto-detected
                      </div>
                    )}
                    {roiData.usage?.reports != null && (
                      <div className="roi-usage-stat">
                        <strong>{roiData.usage.reports.toLocaleString()}</strong> AI reports generated
                      </div>
                    )}
                    {roiData.usage?.activation_rate != null && (
                      <div className="roi-usage-stat">
                        <strong>{Math.round(roiData.usage.activation_rate * 100)}%</strong> activation rate
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div className="roi-empty">
              <div className="roi-empty-icon">$</div>
              <p>Enter personnel count to see ROI calculations</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ROICalculator;
